import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { toast } from '../../hooks/use-toast';
import { messagesAPI } from '../../utils/api';
import {
  Mail, Send, Inbox, ArrowLeft, Reply, Clock,
  Loader2, PenSquare, User, Building2
} from 'lucide-react';

const UniversityMessages = () => {
  const navigate = useNavigate();
  const [view, setView] = useState('inbox');
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [composeData, setComposeData] = useState({ recipientId: '', subject: '', content: '' });
  const [replyContent, setReplyContent] = useState('');
  const [sending, setSending] = useState(false);
  
  const universityId = localStorage.getItem('university_id');
  const universityName = localStorage.getItem('university_name') || 'University';

  useEffect(() => {
    if (!universityId) {
      navigate('/university/login');
      return;
    }
    
    if (view === 'inbox') {
      fetchInbox();
    } else if (view === 'sent') {
      fetchSent();
    }
  }, [view, universityId]);

  const fetchInbox = async () => {
    try {
      setLoading(true);
      const data = await messagesAPI.getUniversityInbox(universityId);
      setMessages(data.messages || []);
      setUnreadCount(data.unread_count || 0);
    } catch (error) {
      console.error('Failed to fetch inbox:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSent = async () => {
    try {
      setLoading(true);
      const data = await messagesAPI.getUniversitySent(universityId);
      setMessages(data.messages || []);
    } catch (error) {
      console.error('Failed to fetch sent:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewMessage = async (message) => {
    try {
      const fullMessage = await messagesAPI.getUniversityMessage(message._id, universityId);
      setSelectedMessage(fullMessage);
      setView('detail');
      if (!message.is_read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to load message", variant: "destructive" });
    }
  };

  const handleSendMessage = async () => {
    if (!composeData.recipientId || !composeData.subject || !composeData.content) {
      toast({ title: "Error", description: "Please fill all fields", variant: "destructive" });
      return;
    }

    try {
      setSending(true);
      await messagesAPI.universitySendMessage(universityId, composeData.recipientId, composeData.subject, composeData.content);
      toast({ title: "Success", description: "Message sent successfully" });
      setComposeData({ recipientId: '', subject: '', content: '' });
      setView('sent');
    } catch (error) {
      toast({ title: "Error", description: "Failed to send message", variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  const handleReply = async () => {
    if (!replyContent.trim()) {
      toast({ title: "Error", description: "Please enter a reply", variant: "destructive" });
      return;
    }

    try {
      setSending(true);
      await messagesAPI.universityReplyMessage(selectedMessage._id, replyContent, universityId);
      toast({ title: "Success", description: "Reply sent successfully" });
      setReplyContent('');
      const updated = await messagesAPI.getUniversityMessage(selectedMessage._id, universityId);
      setSelectedMessage(updated);
    } catch (error) {
      toast({ title: "Error", description: "Failed to send reply", variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <Mail className="w-6 h-6" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">Messages</h1>
                <p className="text-emerald-100 text-sm">{universityName}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="ghost" 
                className="text-white hover:bg-white/10"
                onClick={() => navigate('/university/dashboard')}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid md:grid-cols-4 gap-6">
          {/* Sidebar */}
          <div className="md:col-span-1">
            <Card>
              <CardContent className="p-2">
                <button
                  onClick={() => setView('inbox')}
                  className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${
                    view === 'inbox' || view === 'detail' ? 'bg-emerald-50 text-emerald-700' : 'hover:bg-gray-100'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <Inbox className="h-4 w-4" />
                    Inbox
                  </span>
                  {unreadCount > 0 && (
                    <Badge className="bg-emerald-600">{unreadCount}</Badge>
                  )}
                </button>
                <button
                  onClick={() => setView('sent')}
                  className={`w-full flex items-center gap-2 p-3 rounded-lg transition-colors ${
                    view === 'sent' ? 'bg-emerald-50 text-emerald-700' : 'hover:bg-gray-100'
                  }`}
                >
                  <Send className="h-4 w-4" />
                  Sent
                </button>
                <button
                  onClick={() => setView('compose')}
                  className={`w-full flex items-center gap-2 p-3 rounded-lg transition-colors ${
                    view === 'compose' ? 'bg-emerald-50 text-emerald-700' : 'hover:bg-gray-100'
                  }`}
                >
                  <PenSquare className="h-4 w-4" />
                  Compose
                </button>
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className="md:col-span-3">
            {/* Compose View */}
            {view === 'compose' && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <PenSquare className="h-5 w-5" />
                    New Message to Student
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Student ID</label>
                    <Input
                      placeholder="Enter student ID..."
                      value={composeData.recipientId}
                      onChange={(e) => setComposeData({ ...composeData, recipientId: e.target.value })}
                    />
                    <p className="text-xs text-gray-500 mt-1">Find student IDs from the Applications page</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Subject</label>
                    <Input
                      placeholder="Message subject..."
                      value={composeData.subject}
                      onChange={(e) => setComposeData({ ...composeData, subject: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Message</label>
                    <Textarea
                      placeholder="Write your message..."
                      rows={8}
                      value={composeData.content}
                      onChange={(e) => setComposeData({ ...composeData, content: e.target.value })}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={() => setView('inbox')} variant="outline">Cancel</Button>
                    <Button onClick={handleSendMessage} disabled={sending} className="bg-emerald-600 hover:bg-emerald-700">
                      {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                      Send Message
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Message Detail */}
            {view === 'detail' && selectedMessage && (
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-2 mb-4">
                    <Button variant="ghost" size="sm" onClick={() => setView('inbox')}>
                      <ArrowLeft className="h-4 w-4 mr-1" />
                      Back
                    </Button>
                  </div>
                  <CardTitle>{selectedMessage.subject}</CardTitle>
                  <div className="flex items-center gap-4 text-sm text-gray-600 mt-2">
                    <span className="flex items-center gap-1">
                      <User className="h-4 w-4" />
                      {selectedMessage.sender?.name || 'Student'}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      {formatDate(selectedMessage.created_at)}
                    </span>
                  </div>
                </CardHeader>
                <CardContent>
                  {selectedMessage.thread && selectedMessage.thread.length > 0 ? (
                    <div className="space-y-4 mb-6">
                      {selectedMessage.thread.map((msg, index) => (
                        <div
                          key={index}
                          className={`p-4 rounded-lg ${
                            msg.sender_type === 'university' ? 'bg-emerald-50 ml-8' : 'bg-gray-50 mr-8'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-sm">
                              {msg.sender_type === 'university' ? universityName : selectedMessage.sender?.name}
                            </span>
                            <span className="text-xs text-gray-500">{formatDate(msg.created_at)}</span>
                          </div>
                          <p className="text-gray-700 whitespace-pre-wrap">{msg.content}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-4 bg-gray-50 rounded-lg mb-6">
                      <p className="text-gray-700 whitespace-pre-wrap">{selectedMessage.content}</p>
                    </div>
                  )}

                  <div className="border-t pt-4">
                    <h4 className="font-medium mb-2 flex items-center gap-2">
                      <Reply className="h-4 w-4" />
                      Reply
                    </h4>
                    <Textarea
                      placeholder="Write your reply..."
                      rows={4}
                      value={replyContent}
                      onChange={(e) => setReplyContent(e.target.value)}
                    />
                    <div className="flex justify-end mt-2">
                      <Button onClick={handleReply} disabled={sending} className="bg-emerald-600 hover:bg-emerald-700">
                        {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                        Send Reply
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* List View */}
            {(view === 'inbox' || view === 'sent') && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    {view === 'inbox' ? <Inbox className="h-5 w-5" /> : <Send className="h-5 w-5" />}
                    {view === 'inbox' ? 'Inbox' : 'Sent Messages'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <Mail className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No messages yet</p>
                      {view === 'inbox' && (
                        <p className="text-sm mt-1">Messages from students will appear here</p>
                      )}
                    </div>
                  ) : (
                    <div className="divide-y">
                      {messages.map((message) => (
                        <button
                          key={message._id}
                          onClick={() => handleViewMessage(message)}
                          className={`w-full text-left p-4 hover:bg-gray-50 transition-colors ${
                            !message.is_read && view === 'inbox' ? 'bg-emerald-50/50' : ''
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                {!message.is_read && view === 'inbox' && (
                                  <div className="w-2 h-2 bg-emerald-500 rounded-full" />
                                )}
                                <span className="font-medium truncate">
                                  {view === 'inbox' 
                                    ? message.sender?.name || 'Student' 
                                    : message.recipient?.name || 'Student'}
                                </span>
                                {message.sender?.email && (
                                  <span className="text-xs text-gray-500">({message.sender.email})</span>
                                )}
                              </div>
                              <p className={`truncate ${!message.is_read && view === 'inbox' ? 'font-medium' : ''}`}>
                                {message.subject}
                              </p>
                              <p className="text-sm text-gray-500 truncate">{message.content}</p>
                            </div>
                            <span className="text-xs text-gray-400 ml-4 whitespace-nowrap">
                              {formatDate(message.created_at)}
                            </span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UniversityMessages;
